//react
import React, { useContext, useEffect, useState } from 'react'

//mui
import { CircularProgress, MenuItem, Select, FormControl, InputLabel } from '@mui/material';

//context
import DrawerContext from '../../../context/DrawerContext';
import ModalContext from '../../../context/ModalContext';

//component
import TextInput from '../../../common/TextInput'

import { createAuthor, createPost, listAuthors, listPosts, slugify, uniqueSlug } from '../../blogApi';


const CreateBlogDrawer = () => {

  const modalCtx = useContext(ModalContext);
  const drawerCtx = useContext(DrawerContext);

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [authors, setAuthors] = useState([])
  const [authorSlug, setAuthorSlug] = useState('')
  const [newAuthorName, setNewAuthorName] = useState('')

  // blog-api requires a slug and at least one existing author on create, so
  // both are settled here rather than assigned afterwards like deliva-api's
  // create-then-attach flow.
  const [slugEdited, setSlugEdited] = useState(false)
  const [createBlogObj, setCreateBlogObj] = useState({ snippet: "", title: "", post: "", slug: "", pageTitle: "", metaDescription: "" });

  useEffect(() => {
    listAuthors()
      .then(list => {
        setAuthors(list ?? []);
        if ((list ?? []).length === 1) setAuthorSlug(list[0].slug);
      })
      .catch(err => setError(err.message));
  }, []);

  const handleField = (e) => {
    const { name, value } = e.target;
    setCreateBlogObj((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'title' && !slugEdited ? { slug: slugify(value) } : {}),
    }));
  };

  const handleSlug = (e) => {
    setSlugEdited(true);
    setCreateBlogObj((prev) => ({ ...prev, slug: e.target.value }));
  };

  const submitCreateBlog = async () => {

    setLoading(true);
    setError(null);

    const { snippet, title, post, slug, pageTitle, metaDescription } = createBlogObj;

    try {

      let author = authorSlug;

      if (!author && newAuthorName.trim()) {
        const existingAuthorSlugs = authors.map(a => a.slug);
        const created = await createAuthor({
          slug: uniqueSlug(newAuthorName, existingAuthorSlugs),
          name: newAuthorName.trim(),
        });
        author = created.slug;
      }

      if (!author) {
        setError('Choose an author, or type a name to create one.');
        setLoading(false);
        return;
      }

      // Deleting a post is a soft delete, so its slug stays reserved but is no
      // longer in any listing. Checking the listing therefore cannot rule out a
      // collision — only the 409 can. An auto-generated slug is quietly
      // suffixed until it lands; one the editor typed is theirs to resolve.
      const existing = await listPosts({ pageSize: 100 });
      const takenSlugs = (existing?.data ?? []).map(p => p.slug);

      const body = {
        title,
        content: post || '<p></p>',
        excerpt: snippet || null,
        status: 'draft',
        authorSlugs: [author],
        seo: { title: pageTitle || null, description: metaDescription || null },
      };

      let candidate = uniqueSlug(slug || title, takenSlugs);
      let created = null;

      for (let attempt = 0; attempt < 5 && created === null; attempt++) {
        try {
          created = await createPost({ ...body, slug: candidate });
        } catch (err) {
          if (err.status !== 409) throw err;
          if (slugEdited) {
            setError('That URL slug is already taken — a deleted post may still hold it. Try another.');
            setLoading(false);
            return;
          }
          takenSlugs.push(candidate);
          candidate = uniqueSlug(slug || title, takenSlugs);
        }
      }

      if (!created) {
        setError('Could not find a free URL slug for this title. Set one yourself.');
        setLoading(false);
        return;
      }

      setLoading(false);
      drawerCtx.setPageUpdate({ page: 'Blogs' });
      openUploadImageModal(created);
      drawerCtx.setDetails(null, created);
      drawerCtx.closeDrawer();

    } catch (err) {
      console.error(err);
      setError(err.message);
      setLoading(false);
    }
  }

  const openUploadImageModal = (blog) => {
    modalCtx.setDetails('upload-fixed-image', { aspectRatio: 1.6, origin: 'blog', slug: blog.slug, blog: blog });
    modalCtx.openModal();
  }


  return (
    <div className="drawer-container">
      <div className="drawer">
        <header>
          <h1 data-aos="fade-right" data-aos-delay="500">Create Blog</h1>
        </header>

        <section style={{ padding: '1rem' }}>
          <TextInput name="title" label="Title" value={createBlogObj?.title} onChange={handleField} placeholder="Title" />
          <TextInput name="slug" label="URL Slug" value={createBlogObj?.slug} onChange={handleSlug} placeholder="my-first-post" />
          <TextInput name="snippet" label="Excerpt" value={createBlogObj?.snippet} onChange={handleField} placeholder="Short summary" />

          <FormControl fullWidth size="small" style={{ margin: '0.5rem 0 1rem' }}>
            <InputLabel id="create-blog-author-label">Author</InputLabel>
            <Select
              labelId="create-blog-author-label"
              label="Author"
              value={authorSlug}
              onChange={(e) => setAuthorSlug(e.target.value)}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {authors.map((a) => <MenuItem key={a.slug} value={a.slug}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>

          {!authorSlug && (
            <TextInput
              name="newAuthorName"
              label="…or create an author"
              value={newAuthorName}
              onChange={(e) => setNewAuthorName(e.target.value)}
              placeholder="Author name"
            />
          )}

          <TextInput name="pageTitle" label="Page Title" value={createBlogObj?.pageTitle} onChange={handleField} placeholder="Page Title" />
          <TextInput name="metaDescription" label="Meta Description" value={createBlogObj?.metaDescription} onChange={handleField} placeholder="Meta Description" />

          {error ? <p style={{ color: '#d32f2f', fontSize: '0.85rem' }}>{error}</p> : null}

          <div className="double-column-form aligned-right margin-top">
            <button
              disabled={loading || !createBlogObj?.title.length || (!authorSlug && !newAuthorName.trim())}
              className='btn-primary'
              onClick={() => submitCreateBlog()}
            >
              {loading ? <CircularProgress size={22} style={{ color: "#fff" }} /> : "Save Changes"}
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}

export default CreateBlogDrawer;
